import React, { useState, useEffect } from 'react';
import { submitSignup } from '../api/signup';

const GetStartedModal = ({ isOpen, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        organizationType: '',
        city: '',
        organizationName: '',
        role: '',
        message: ''
    });
    const [showOptional, setShowOptional] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            console.log('Modal opened');
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
            // Reset form when modal closes
            setFormData({
                fullName: '',
                email: '',
                organizationType: '',
                city: '',
                organizationName: '',
                role: '',
                message: ''
            });
            setErrors({});
            setIsSubmitted(false);
            setShowOptional(false);
        }
    }, [isOpen]);

    // Close modal on ESC key press
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Reset errors
        setErrors({});
        setIsLoading(true);

        // Validate required fields
        const newErrors = {};

        if (!formData.fullName.trim()) {
            newErrors.fullName = 'Full Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!validateEmail(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.organizationType) {
            newErrors.organizationType = 'Organization Type is required';
        }

        if (!formData.city.trim()) {
            newErrors.city = 'City is required';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setIsLoading(false);
            console.error('Validation error:', newErrors);
            return;
        }

        try {
            const result = await submitSignup(formData);

            if (result.ok) {
                // Success (200)
                setIsSubmitted(true);
                // Reset form
                setFormData({
                    fullName: '',
                    email: '',
                    organizationType: '',
                    city: '',
                    organizationName: '',
                    role: '',
                    message: ''
                });
                setShowOptional(false);
                // Call onSuccess callback if provided
                if (onSuccess) {
                    onSuccess();
                }
            } else if (result.status === 409) {
                // Duplicate email
                setErrors({ email: 'This email has already been registered.' });
            } else if (result.status === 400) {
                // Validation errors from server
                if (result.data.message) {
                    setErrors({ submit: result.data.message });
                } else if (result.data.error) {
                    setErrors({ submit: result.data.error });
                } else {
                    setErrors({ submit: 'Validation error. Please check your input.' });
                }
            } else if (result.status === 500) {
                // Server error
                setErrors({ submit: 'Something went wrong. Please try again later.' });
            } else {
                // Other errors
                setErrors({ submit: result.data.message || 'An error occurred. Please try again.' });
            }
        } catch (err) {
            console.error('API Error:', err);
            setErrors({ submit: 'Something went wrong. Please try again later.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="get-started-modal-overlay" onClick={handleBackdropClick}>
            <div className="get-started-modal">
                <button className="get-started-modal-close" onClick={onClose}>×</button>
                
                {!isSubmitted ? (
                    <>
                        <h3>meDZhub</h3>
                        <p className="modal-subtitle">Fill in your details and we'll get back to you soon.</p>
                        
                        <form onSubmit={handleSubmit} className="get-started-form">
                            <div className="form-group">
                                <label htmlFor="fullName">Full Name <span className="required">*</span></label>
                                <input
                                    type="text"
                                    id="fullName"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleChange}
                                    className={errors.fullName ? 'error' : ''}
                                    placeholder="Enter your full name"
                                />
                                {errors.fullName && <span className="error-message">{errors.fullName}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email <span className="required">*</span></label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={errors.email ? 'error' : ''}
                                    placeholder="your.email@example.com"
                                />
                                {errors.email && <span className="error-message">{errors.email}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="organisationType">Organization Type <span className="required">*</span></label>
                                <select
                                    id="organizationType"
                                    name="organizationType"
                                    value={formData.organizationType}
                                    onChange={handleChange}
                                    className={errors.organizationType ? 'error' : ''}
                                >
                                    <option value="">Select organisation type</option>
                                    <option value="Rehab Center">Rehab Centre</option>
                                    <option value="Therapist">Therapist</option>
                                    <option value="Distributor">Distributor</option>
                                    <option value="Doctor">Doctor</option>
                                    <option value="Pharmacy / Retail">Pharmacy / Retail</option>
                                    <option value="Clinic">Clinic</option>
                                    <option value="Patient">Patient</option>
                                    <option value="Individual / Just exploring">Individual / Just exploring</option>
                                </select>
                                {errors.organizationType && <span className="error-message">{errors.organizationType}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="city">City <span className="required">*</span></label>
                                <input
                                    type="text"
                                    id="city"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    className={errors.city ? 'error' : ''}
                                    placeholder="Enter your city"
                                />
                                {errors.city && <span className="error-message">{errors.city}</span>}
                            </div>

                            <div className="optional-section">
                                <button
                                    type="button"
                                    className="optional-toggle"
                                    onClick={() => setShowOptional(!showOptional)}
                                >
                                    {showOptional ? '▼' : '▶'} More details (optional)
                                </button>

                                {showOptional && (
                                    <div className="optional-fields">
                                        <div className="form-group">
                                            <label htmlFor="organizationName">Organization Name</label>
                                            <input
                                                type="text"
                                                id="organizationName"
                                                name="organizationName"
                                                value={formData.organizationName}
                                                onChange={handleChange}
                                                placeholder="Enter organization name"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="role">Role / Position</label>
                                            <input
                                                type="text"
                                                id="role"
                                                name="role"
                                                value={formData.role}
                                                onChange={handleChange}
                                                placeholder="Enter your role or position"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="message">Message</label>
                                            <textarea
                                                id="message"
                                                name="message"
                                                value={formData.message}
                                                onChange={handleChange}
                                                rows="4"
                                                placeholder="Any additional information you'd like to share..."
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {errors.submit && <span className="error-message submit-error">{errors.submit}</span>}

                            <div className="form-actions">
                                <button 
                                    type="submit" 
                                    className="btn btn-primary" 
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Submitting...' : 'Submit'}
                                </button>
                            </div>
                        </form>
                    </>
                ) : (
                    <div className="success-state">
                        <div className="success-icon">✓</div>
                        <h3>Thank you!</h3>
                        <p>We'll reach out to you soon.</p>
                        <button onClick={onClose} className="btn btn-primary">Close</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GetStartedModal;

